import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Input,
  Select,
  HStack,
  VStack,
  Spinner,
  Center,
  Text,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  useDisclosure,
  IconButton,
  Tooltip,
  Textarea,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Grid,
  GridItem,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Divider,
  Tag,
  TagLabel,
  TagLeftIcon,
} from '@chakra-ui/react';
import { 
  FiDollarSign, 
  FiEye, 
  FiRotateCcw, 
  FiCalendar,
  FiCheck,
  FiX,
  FiAlertCircle,
  FiEdit2
} from 'react-icons/fi';
import { 
  getAllPaymentsWithCustomer, 
  PaymentWithCustomer, 
  processRefund, 
  updatePayment,
  getAllBookingsWithCustomer,
  BookingWithCustomer
} from '../services/firestore';
import { secureStripeService } from '../services/firebaseFunctions';
import { useAuth } from '../contexts/AuthContext';

const formatAmount = (amount: number | undefined) => `$${((amount ?? 0) / 100).toFixed(2)}`;

const PaymentManagement: React.FC = () => {
  const { currentUser, isAdmin } = useAuth();
  const [payments, setPayments] = useState<PaymentWithCustomer[]>([]);
  const [filteredPayments, setFilteredPayments] = useState<PaymentWithCustomer[]>([]);
  const [bookings, setBookings] = useState<BookingWithCustomer[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [selectedPayment, setSelectedPayment] = useState<PaymentWithCustomer | null>(null);
  const [refundAmount, setRefundAmount] = useState<number>(0);
  const [refundReason, setRefundReason] = useState('');
  const [processingRefund, setProcessingRefund] = useState(false);
  const [updatingPayment, setUpdatingPayment] = useState(false);
  const [editingNotes, setEditingNotes] = useState('');
  
  // Process payment form state
  const [isProcessOpen, setIsProcessOpen] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [processForm, setProcessForm] = useState({
    customerId: '',
    customerName: '',
    customerEmail: '',
    amount: '',
    currency: 'usd',
    paymentMethod: 'card',
    bookingId: '',
    description: '',
    metadata: {}
  });
  
  const { isOpen: isViewOpen, onOpen: onViewOpen, onClose: onViewClose } = useDisclosure();
  const { isOpen: isRefundOpen, onOpen: onRefundOpen, onClose: onRefundClose } = useDisclosure();
  const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure();
  const toast = useToast();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchData();
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    filterPayments();
  }, [payments, searchTerm, statusFilter, dateFilter]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [paymentsData, bookingsData] = await Promise.all([
        getAllPaymentsWithCustomer(),
        getAllBookingsWithCustomer(),
      ]);
      
      setPayments(paymentsData);
      setBookings(bookingsData);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast({
        title: 'Error loading payments',
        description: 'Failed to fetch payments data',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const filterPayments = () => {
    let filtered = payments;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(payment => {
        const customerName = getCustomerName(payment);
        const transactionId = payment.transactionId || '';
        const paymentId = payment.id || '';
        const bookingId = payment.bookingId || '';
        
        return customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
               transactionId.toLowerCase().includes(searchTerm.toLowerCase()) ||
               paymentId.toLowerCase().includes(searchTerm.toLowerCase()) ||
               bookingId.toLowerCase().includes(searchTerm.toLowerCase());
      });
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(payment => payment.status === statusFilter);
    }

    // Date filter
    if (dateFilter !== 'all') {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      
      switch (dateFilter) {
        case 'today':
          filtered = filtered.filter(payment => {
            const paymentDate = payment.createdAt.toDate ? payment.createdAt.toDate() : new Date(payment.createdAt.seconds * 1000);
            return paymentDate >= today;
          });
          break;
        case 'week':
          const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
          filtered = filtered.filter(payment => {
            const paymentDate = payment.createdAt.toDate ? payment.createdAt.toDate() : new Date(payment.createdAt.seconds * 1000);
            return paymentDate >= weekAgo;
          });
          break;
        case 'month':
          const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
          filtered = filtered.filter(payment => {
            const paymentDate = payment.createdAt.toDate ? payment.createdAt.toDate() : new Date(payment.createdAt.seconds * 1000);
            return paymentDate >= monthAgo;
          });
          break;
      }
    }

    setFilteredPayments(filtered);
  };

  const getCustomerName = (payment: PaymentWithCustomer) => {
    if (payment.customer?.firstName && payment.customer?.lastName) {
      return `${payment.customer.firstName} ${payment.customer.lastName}`;
    } else if (payment.customer?.firstName) {
      return payment.customer.firstName;
    } else if (payment.customer?.lastName) {
      return payment.customer.lastName;
    } else if (payment.customer?.email) {
      return payment.customer.email;
    } else {
      return 'Unknown Customer';
    }
  };

  const handleViewPayment = (payment: PaymentWithCustomer) => {
    setSelectedPayment(payment);
    onViewOpen();
  };

  const handleRefundPayment = (payment: PaymentWithCustomer) => {
    setSelectedPayment(payment);
    // Convert from cents to dollars for the UI
    setRefundAmount(payment.amount / 100);
    setRefundReason('');
    onRefundOpen();
  };

  const handleEditPayment = (payment: PaymentWithCustomer) => {
    setSelectedPayment(payment);
    setEditingNotes(payment.notes || '');
    onEditOpen();
  };

  const processRefundPayment = async () => {
    // Check if user is authenticated as admin
    if (!currentUser || !isAdmin) {
      toast({
        title: 'Access Denied',
        description: 'Admin privileges required to process refunds',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    if (!selectedPayment || refundAmount <= 0) return;

    // Validate refund amount (refundAmount is in dollars, selectedPayment.amount is in cents)
    const refundAmountInCents = Math.round(refundAmount * 100);
    const paymentAmountInCents = selectedPayment.amount;
    const existingRefundAmount = selectedPayment.refundAmount || 0;
    const remainingAmount = paymentAmountInCents - existingRefundAmount;

    if (refundAmountInCents > remainingAmount) {
      toast({
        title: 'Invalid Refund Amount',
        description: `Refund amount cannot exceed remaining amount of $${(remainingAmount / 100).toFixed(2)}`,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    try {
      setProcessingRefund(true);
      
      let stripeRefundId: string | undefined = undefined;
      
      // If payment has Stripe payment intent ID, process refund through Stripe via Firebase function
      // This is the ONLY place we should process Stripe refunds (not in firestore.ts)
      if (selectedPayment.stripePaymentIntentId) {
        try {
          const stripeResult = await secureStripeService.processRefund({
            paymentIntentId: selectedPayment.stripePaymentIntentId,
            amount: refundAmount, // Amount in dollars - Firebase function will convert to cents
            reason: refundReason || 'requested_by_customer',
            metadata: {
              paymentId: selectedPayment.id,
              bookingId: selectedPayment.bookingId,
              refundReason: refundReason
            }
          });
          
          stripeRefundId = stripeResult.refund.id || undefined;
        } catch (stripeError) {
          console.error('Stripe refund failed:', stripeError);
          toast({
            title: 'Stripe Refund Failed',
            description: 'Stripe refund failed, but continuing with Firestore update',
            status: 'warning',
            duration: 5000,
            isClosable: true,
          });
          // Continue with Firestore update even if Stripe fails
        }
      }
      
      // Update Firestore payment record (refundAmount should be in cents)
      // NOTE: processRefund in firestore.ts should NOT process Stripe refunds - that's done above
      await processRefund(
        selectedPayment.id,
        refundAmountInCents, // Convert to cents for Firestore
        refundReason,
        currentUser.email || 'admin',
        stripeRefundId // Pass the Stripe refund ID if available
      );

      // Update local state
      const totalRefunded = (existingRefundAmount + refundAmountInCents);
      const isFullRefund = totalRefunded >= paymentAmountInCents;
      
      const updatedPayment: PaymentWithCustomer = {
        ...selectedPayment,
        status: isFullRefund ? 'refunded' : 'partially_refunded',
        refundAmount: totalRefunded, // Total refunded amount in cents
        refundReason: refundReason,
        refundedAt: new Date() as any,
        refundedBy: currentUser.email || 'admin',
        stripeRefundId: stripeRefundId,
        updatedAt: new Date() as any
      };

      setPayments(payments.map(payment => 
        payment.id === selectedPayment.id ? updatedPayment : payment
      ));

      const refundType = isFullRefund ? 'full' : 'partial';
      const stripeInfo = stripeRefundId ? ' (processed via Stripe)' : ' (local refund)';
      
      toast({
        title: `${refundType.charAt(0).toUpperCase() + refundType.slice(1)} refund processed`,
        description: `Refund of $${refundAmount.toFixed(2)} has been processed successfully${stripeInfo}`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      
      onRefundClose();
    } catch (error) {
      console.error('Error processing refund:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to process refund';
      
      toast({
        title: 'Refund failed',
        description: errorMessage,
        status: 'error',
        duration: 8000,
        isClosable: true,
      });
    } finally {
      setProcessingRefund(false);
    }
  };

  const handleUpdatePayment = async () => {
    if (!selectedPayment) return;

    try {
      setUpdatingPayment(true);
      
      await updatePayment(selectedPayment.id, {
        notes: editingNotes
      });

      // Update local state
      setPayments(payments.map(payment => 
        payment.id === selectedPayment.id 
          ? { ...payment, notes: editingNotes, updatedAt: new Date() as any }
          : payment
      ));

      toast({
        title: 'Payment updated',
        description: 'Payment has been updated successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
      onEditClose();
    } catch (error) {
      console.error('Error updating payment:', error);
      toast({
        title: 'Update failed',
        description: 'Failed to update payment',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setUpdatingPayment(false);
    }
  };

  const handleProcessPayment = async () => {
    // Check if user is authenticated as admin
    if (!currentUser || !isAdmin) {
      toast({
        title: 'Access Denied',
        description: 'Admin privileges required to process payments',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    try {
      setProcessing(true);
      
      // Validate required fields
      if (!processForm.customerName || !processForm.amount || !processForm.customerEmail) {
        toast({
          title: 'Validation Error',
          description: 'Please fill in all required fields',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      const amount = parseFloat(processForm.amount);
      if (amount <= 0) {
        toast({
          title: 'Invalid Amount',
          description: 'Amount must be greater than 0',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      // Create or retrieve customer
      let customerId = processForm.customerId;
      if (!customerId) {
        try {
          const customerResult = await secureStripeService.createOrRetrieveCustomer({
            email: processForm.customerEmail,
            name: processForm.customerName,
            metadata: {
              createdBy: 'admin-web',
              bookingId: processForm.bookingId || 'manual'
            }
          });
          customerId = customerResult.customer.id;
        } catch (customerError) {
          console.error('Error creating customer:', customerError);
          // Continue without customer ID if creation fails
        }
      }

      // Create payment intent
      console.log('Creating payment intent with data:', {
        amount: amount,
        currency: processForm.currency,
        customerId: customerId,
        bookingId: processForm.bookingId || 'manual',
        metadata: {
          customerName: processForm.customerName,
          customerEmail: processForm.customerEmail,
          description: processForm.description,
          createdBy: 'admin-web',
          ...processForm.metadata
        }
      });

      const paymentIntentResult = await secureStripeService.createPaymentIntent({
        amount: amount,
        currency: processForm.currency,
        customerId: customerId,
        bookingId: processForm.bookingId || 'manual',
        metadata: {
          customerName: processForm.customerName,
          customerEmail: processForm.customerEmail,
          description: processForm.description,
          createdBy: 'admin-web',
          ...processForm.metadata
        }
      });

      console.log('Payment intent created successfully:', paymentIntentResult);

      // Create payment record in Firestore
      // Note: amount should be in cents to match Firestore schema
      // paymentIntentResult.paymentIntent.amount is already in cents from Stripe
      const newPayment: PaymentWithCustomer = {
        id: `payment_${Date.now()}`,
        bookingId: processForm.bookingId || 'manual',
        customerId: customerId || 'unknown',
        customerName: processForm.customerName,
        amount: paymentIntentResult.paymentIntent.amount, // Use amount from Stripe (in cents)
        status: 'pending',
        paymentMethod: processForm.paymentMethod,
        transactionId: paymentIntentResult.paymentIntent.id,
        stripePaymentIntentId: paymentIntentResult.paymentIntent.id,
        notes: processForm.description,
        createdAt: new Date() as any,
        updatedAt: new Date() as any
      };

      // Add to local state
      setPayments([newPayment, ...payments]);

      // Reset form
      setProcessForm({
        customerId: '',
        customerName: '',
        customerEmail: '',
        amount: '',
        currency: 'usd',
        paymentMethod: 'card',
        bookingId: '',
        description: '',
        metadata: {}
      });

      setIsProcessOpen(false);

      // Show success message with payment link if available
      const paymentLinkUrl = paymentIntentResult.paymentLink?.url;
      const toastDescription = paymentLinkUrl
        ? `Payment link created successfully.`
        : `Payment intent created. Client secret: ${paymentIntentResult.paymentIntent.clientSecret?.substring(0, 20)}...`;

      toast({
        title: 'Payment Created Successfully',
        description: (
          <Box>
            <Text mb={2}>{toastDescription}</Text>
            {paymentLinkUrl && (
              <VStack spacing={2} align="stretch">
                <HStack spacing={2}>
                  <Button
                    size="sm"
                    onClick={() => {
                      navigator.clipboard.writeText(paymentLinkUrl);
                      toast({
                        title: 'Copied!',
                        description: 'Payment link copied to clipboard',
                        status: 'success',
                        duration: 2000,
                      });
                    }}
                  >
                    Copy Payment Link
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    as="a"
                    href={paymentLinkUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Open Link
                  </Button>
                </HStack>
                {processForm.customerEmail && (
                  <Button
                    size="sm"
                    colorScheme="teal"
                    onClick={async () => {
                      try {
                        await secureStripeService.sendPaymentLinkEmail({
                          customerEmail: processForm.customerEmail,
                          customerName: processForm.customerName,
                          amount: paymentIntentResult.paymentIntent.amount,
                          paymentLinkUrl: paymentLinkUrl,
                          description: processForm.description || undefined,
                        });
                        toast({
                          title: 'Email Sent!',
                          description: `Payment link sent to ${processForm.customerEmail}`,
                          status: 'success',
                          duration: 5000,
                        });
                      } catch (error) {
                        console.error('Error sending email:', error);
                        toast({
                          title: 'Error Sending Email',
                          description: error instanceof Error ? error.message : 'Failed to send email',
                          status: 'error',
                          duration: 5000,
                        });
                      }
                    }}
                  >
                    📧 Email Payment Link
                  </Button>
                )}
              </VStack>
            )}
          </Box>
        ),
        status: 'success',
        duration: 15000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error processing payment:', error);
      
      // Extract more detailed error information
      let errorMessage = 'Failed to process payment';
      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === 'object' && error !== null) {
        // Handle Firebase Function errors
        const firebaseError = error as any;
        if (firebaseError.details) {
          errorMessage = firebaseError.details;
        } else if (firebaseError.message) {
          errorMessage = firebaseError.message;
        } else if (firebaseError.code) {
          errorMessage = `Error ${firebaseError.code}: ${firebaseError.message || 'Unknown error'}`;
        }
      }
      
      toast({
        title: 'Payment Processing Failed',
        description: errorMessage,
        status: 'error',
        duration: 8000,
        isClosable: true,
      });
    } finally {
      setProcessing(false);
    }
  };

  const handleProcessFormChange = (field: string, value: string) => {
    setProcessForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'green';
      case 'pending':
        return 'yellow';
      case 'failed':
        return 'red';
      case 'refunded':
        return 'purple';
      case 'partially_refunded':
        return 'orange';
      default:
        return 'gray';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return FiCheck;
      case 'pending':
        return FiCalendar;
      case 'failed':
        return FiX;
      case 'refunded':
      case 'partially_refunded':
        return FiRotateCcw;
      default:
        return FiAlertCircle;
    }
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getBookingInfo = (bookingId: string) => {
    const booking = bookings.find(b => b.id === bookingId);
    return booking ? `${booking.serviceType} - ${booking.customerName}` : 'Booking not found';
  };

  const getPaymentStats = () => {
    const stats = {
      total: 0,
      completed: 0,
      pending: 0,
      failed: 0,
      refunded: 0,
      partiallyRefunded: 0
    };

    filteredPayments.forEach(payment => {
      stats.total += payment.amount;
      
      switch (payment.status) {
        case 'completed':
          stats.completed += payment.amount;
          break;
        case 'pending':
          stats.pending += payment.amount;
          break;
        case 'failed':
          stats.failed += payment.amount;
          break;
        case 'refunded':
          stats.refunded += payment.amount;
          break;
        case 'partially_refunded':
          stats.partiallyRefunded += payment.amount;
          break;
      }
    });

    return stats;
  };

  const paymentStats = getPaymentStats();

  if (loading) {
    return (
      <Center h="400px">
        <Spinner size="xl" color="blue.500" />
      </Center>
    );
  }

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={6} flexWrap="wrap" gap={4}>
        <Heading 
          size={{ base: 'md', md: 'lg' }}
          pl={{ base: '60px', lg: 0 }}
          pr={{ base: 4, lg: 0 }}
          flex="1"
          minW="200px"
        >
          Payment Management
        </Heading>
        <Tooltip 
          label={!isAdmin ? "Admin privileges required to process payments" : "Process a new payment"}
          isDisabled={isAdmin}
        >
          <Button 
            leftIcon={React.createElement(FiDollarSign as React.ElementType)} 
            colorScheme="green"
            onClick={() => setIsProcessOpen(true)}
            isDisabled={!isAdmin}
          >
            Process Payment
          </Button>
        </Tooltip>
      </Flex>

      {/* Admin Access Warning */}
      {!isAdmin && (
        <Alert status="warning" mb={6}>
          <AlertIcon />
          <Box>
            <AlertTitle>Limited Access</AlertTitle>
            <AlertDescription>
              You need admin privileges to process payments and refunds. Please contact your administrator.
            </AlertDescription>
          </Box>
        </Alert>
      )}

      {/* Payment Statistics */}
      <Grid templateColumns="repeat(3, 1fr)" gap={4} mb={6}>
        <GridItem>
          <Stat>
            <StatLabel>Total Revenue</StatLabel>
            <StatNumber color="green.500">{formatAmount(paymentStats.total)}</StatNumber>
            <StatHelpText>All payments</StatHelpText>
          </Stat>
        </GridItem>
        <GridItem>
          <Stat>
            <StatLabel>Completed Payments</StatLabel>
            <StatNumber color="blue.500">{formatAmount(paymentStats.completed)}</StatNumber>
            <StatHelpText>Successful transactions</StatHelpText>
          </Stat>
        </GridItem>
        <GridItem>
          <Stat>
            <StatLabel>Refunds</StatLabel>
            <StatNumber color="red.500">{formatAmount(paymentStats.refunded + paymentStats.partiallyRefunded)}</StatNumber>
            <StatHelpText>Total refunded amount</StatHelpText>
          </Stat>
        </GridItem>
      </Grid>

      {/* Search and Filter Controls */}
      <Box bg="white" p={4} borderRadius="lg" boxShadow="sm" mb={6}>
        <HStack spacing={4}>
          <Box flex={1}>
            <FormLabel fontSize="sm">Search Payments</FormLabel>
            <Input
              placeholder="Search by customer, transaction ID, or booking ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </Box>
          <Box minW="200px">
            <FormLabel fontSize="sm">Filter by Status</FormLabel>
            <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="all">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="completed">Completed</option>
              <option value="failed">Failed</option>
              <option value="refunded">Refunded</option>
              <option value="partially_refunded">Partially Refunded</option>
            </Select>
          </Box>
          <Box minW="200px">
            <FormLabel fontSize="sm">Filter by Date</FormLabel>
            <Select value={dateFilter} onChange={(e) => setDateFilter(e.target.value)}>
              <option value="all">All Time</option>
              <option value="today">Today</option>
              <option value="week">Last 7 Days</option>
              <option value="month">Last 30 Days</option>
            </Select>
          </Box>
        </HStack>
      </Box>

      {/* Payments Table */}
      <Box bg="white" p={6} borderRadius="lg" boxShadow="sm">
        <Box 
          overflowX="auto" 
          overflowY="auto"
          maxH="calc(100vh - 400px)"
          border="1px solid"
          borderColor="gray.200"
          borderRadius="md"
        >
          <Table variant="simple" minW="100%">
          <Thead>
            <Tr>
              <Th>Transaction ID</Th>
              <Th>Customer</Th>
              <Th>Booking</Th>
              <Th>Booking ID</Th>
              <Th>Amount</Th>
              <Th>Status</Th>
              <Th>Payment Method</Th>
              <Th>Date</Th>
              <Th>Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {filteredPayments.map((payment) => (
              <Tr key={payment.id}>
                <Td fontWeight="bold">#{payment.transactionId || payment.id.slice(-8)}</Td>
                <Td>{getCustomerName(payment)}</Td>
                <Td>
                  <Text fontSize="sm" color="gray.600">
                    {getBookingInfo(payment.bookingId)}
                  </Text>
                </Td>
                <Td>
                  {payment.bookingId ? (
                    <Tooltip label="Click to copy booking ID">
                      <Text 
                        fontSize="sm" 
                        fontFamily="mono" 
                        color="blue.500" 
                        cursor="pointer"
                        onClick={() => {
                          navigator.clipboard.writeText(payment.bookingId);
                          toast({
                            title: 'Copied',
                            description: 'Booking ID copied to clipboard',
                            status: 'success',
                            duration: 2000,
                            isClosable: true,
                          });
                        }}
                        _hover={{ textDecoration: 'underline' }}
                      >
                        {payment.bookingId}
                      </Text>
                    </Tooltip>
                  ) : (
                    <Text fontSize="sm" color="gray.400">N/A</Text>
                  )}
                </Td>
                <Td>
                  <Text fontWeight="bold">{formatAmount(payment.amount)}</Text>
                  {payment.refundAmount && (
                    <Text fontSize="xs" color="red.500">
                      Refunded: {formatAmount(payment.refundAmount)}
                    </Text>
                  )}
                </Td>
                <Td>
                  <Tag colorScheme={getStatusColor(payment.status)} size="sm">
                    <TagLeftIcon as={getStatusIcon(payment.status) as React.ElementType} />
                    <TagLabel>{payment.status.replace('_', ' ')}</TagLabel>
                  </Tag>
                </Td>
                <Td>{payment.paymentMethod}</Td>
                <Td>{formatDate(payment.createdAt)}</Td>
                <Td>
                  <HStack spacing={2}>
                    <Tooltip label="View Details">
                      <IconButton
                        size="sm"
                        icon={React.createElement(FiEye as React.ElementType)}
                        aria-label="View payment"
                        variant="ghost"
                        onClick={() => handleViewPayment(payment)}
                      />
                    </Tooltip>
                    <Tooltip label="Edit Payment">
                      <IconButton
                        size="sm"
                        icon={React.createElement(FiEdit2 as React.ElementType)}
                        aria-label="Edit payment"
                        variant="ghost"
                        onClick={() => handleEditPayment(payment)}
                      />
                    </Tooltip>
                    {(payment.status === 'completed' || payment.status === 'partially_refunded') && (
                      <Tooltip label="Process Refund">
                        <IconButton
                          size="sm"
                          icon={React.createElement(FiRotateCcw as React.ElementType)}
                          aria-label="Process refund"
                          variant="ghost"
                          colorScheme="red"
                          onClick={() => handleRefundPayment(payment)}
                        />
                      </Tooltip>
                    )}
                  </HStack>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
        </Box>

        {filteredPayments.length === 0 && (
          <Center py={8}>
            <Text color="gray.500">No payments found</Text>
          </Center>
        )}
      </Box>

      {/* View Payment Modal */}
      <Modal isOpen={isViewOpen} onClose={onViewClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Payment Details</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {selectedPayment && (
              <VStack spacing={4} align="stretch">
                <Box>
                  <Text fontWeight="bold" fontSize="lg">
                    Transaction #{selectedPayment.transactionId || selectedPayment.id.slice(-8)}
                  </Text>
                  <Text color="gray.600">{getCustomerName(selectedPayment)}</Text>
                </Box>
                
                <HStack>
                  <Box flex={1}>
                    <Text fontWeight="semibold">Amount</Text>
                    <Text fontSize="xl" fontWeight="bold" color="green.500">
                      {formatAmount(selectedPayment.amount)}
                    </Text>
                  </Box>
                  <Box flex={1}>
                    <Text fontWeight="semibold">Status</Text>
                    <Tag colorScheme={getStatusColor(selectedPayment.status)} size="md">
                      <TagLeftIcon as={getStatusIcon(selectedPayment.status) as React.ElementType} />
                      <TagLabel>{selectedPayment.status.replace('_', ' ')}</TagLabel>
                    </Tag>
                  </Box>
                </HStack>

                <HStack>
                  <Box flex={1}>
                    <Text fontWeight="semibold">Payment Method</Text>
                    <Text>{selectedPayment.paymentMethod}</Text>
                  </Box>
                  <Box flex={1}>
                    <Text fontWeight="semibold">Date</Text>
                    <Text>{formatDate(selectedPayment.createdAt)}</Text>
                  </Box>
                </HStack>

                {selectedPayment.stripePaymentIntentId && (
                  <Box>
                    <Text fontWeight="semibold">Stripe Payment Intent</Text>
                    <Text fontSize="sm" color="gray.600" fontFamily="mono">
                      {selectedPayment.stripePaymentIntentId}
                    </Text>
                  </Box>
                )}

                <HStack>
                  <Box flex={1}>
                    <Text fontWeight="semibold">Booking</Text>
                    <Text>{getBookingInfo(selectedPayment.bookingId)}</Text>
                  </Box>
                  {selectedPayment.bookingId && (
                    <Box flex={1}>
                      <Text fontWeight="semibold">Booking ID</Text>
                      <Tooltip label="Click to copy booking ID">
                        <Text 
                          fontSize="sm" 
                          fontFamily="mono" 
                          color="blue.500" 
                          cursor="pointer"
                          onClick={() => {
                            navigator.clipboard.writeText(selectedPayment.bookingId);
                            toast({
                              title: 'Copied',
                              description: 'Booking ID copied to clipboard',
                              status: 'success',
                              duration: 2000,
                              isClosable: true,
                            });
                          }}
                          _hover={{ textDecoration: 'underline' }}
                        >
                          {selectedPayment.bookingId}
                        </Text>
                      </Tooltip>
                    </Box>
                  )}
                </HStack>

                {selectedPayment.refundAmount && (
                  <>
                    <Divider />
                    <Box>
                      <Text fontWeight="semibold" color="red.500">Refund Information</Text>
                      <VStack align="stretch" spacing={2} mt={2}>
                        <HStack>
                          <Text fontSize="sm">Refund Amount:</Text>
                          <Text fontSize="sm" fontWeight="bold" color="red.500">
                            {formatAmount(selectedPayment.refundAmount)}
                          </Text>
                        </HStack>
                        <HStack>
                          <Text fontSize="sm">Reason:</Text>
                          <Text fontSize="sm">{selectedPayment.refundReason}</Text>
                        </HStack>
                        <HStack>
                          <Text fontSize="sm">Refunded By:</Text>
                          <Text fontSize="sm">{selectedPayment.refundedBy}</Text>
                        </HStack>
                        <HStack>
                          <Text fontSize="sm">Refund Date:</Text>
                          <Text fontSize="sm">{formatDate(selectedPayment.refundedAt)}</Text>
                        </HStack>
                        {selectedPayment.stripeRefundId && (
                          <HStack>
                            <Text fontSize="sm">Stripe Refund ID:</Text>
                            <Text fontSize="sm" fontFamily="mono" color="gray.600">
                              {selectedPayment.stripeRefundId}
                            </Text>
                          </HStack>
                        )}
                      </VStack>
                    </Box>
                  </>
                )}

                {selectedPayment.notes && (
                  <>
                    <Divider />
                    <Box>
                      <Text fontWeight="semibold">Notes</Text>
                      <Text>{selectedPayment.notes}</Text>
                    </Box>
                  </>
                )}
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" onClick={onViewClose}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Refund Modal */}
      <Modal isOpen={isRefundOpen} onClose={onRefundClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Process Refund</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {selectedPayment && (
              <VStack spacing={4}>
                <Alert status="info">
                  <AlertIcon />
                  <Box>
                    <AlertTitle>Refund Payment</AlertTitle>
                    <AlertDescription>
                      Processing refund for payment of {formatAmount(selectedPayment.amount)}
                    </AlertDescription>
                  </Box>
                </Alert>

                <FormControl>
                  <FormLabel>Refund Amount</FormLabel>
                  <NumberInput
                    value={refundAmount}
                    onChange={(_, value) => setRefundAmount(value)}
                    min={0}
                    max={selectedPayment.amount / 100} // Convert from cents to dollars
                    precision={2}
                  >
                    <NumberInputField />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                </FormControl>

                <FormControl>
                  <FormLabel>Refund Reason</FormLabel>
                  <Textarea
                    value={refundReason}
                    onChange={(e) => setRefundReason(e.target.value)}
                    placeholder="Enter reason for refund..."
                    rows={3}
                  />
                </FormControl>
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onRefundClose}>
              Cancel
            </Button>
            <Button 
              colorScheme="red" 
              onClick={processRefundPayment}
              isLoading={processingRefund}
              loadingText="Processing"
              isDisabled={refundAmount <= 0 || refundAmount > ((selectedPayment?.amount || 0) / 100)}
            >
              Process Refund
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Edit Payment Modal */}
      <Modal isOpen={isEditOpen} onClose={onEditClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Edit Payment</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              {selectedPayment && (
                <>
                  {selectedPayment.id.startsWith('booking_') && !selectedPayment.stripeCheckoutSessionId && (
                    <Alert status="info" size="sm">
                      <AlertIcon />
                      <AlertDescription fontSize="sm">
                        This payment was processed outside of Stripe (cash, check, bank transfer, etc.). 
                        Use this field to add notes about the payment method or any special circumstances.
                      </AlertDescription>
                    </Alert>
                  )}
                  
                  {selectedPayment.stripeCheckoutSessionId && (
                    <Alert status="success" size="sm">
                      <AlertIcon />
                      <AlertDescription fontSize="sm">
                        This payment was processed via Stripe Checkout Session. 
                        Notes added here are for internal reference only.
                      </AlertDescription>
                    </Alert>
                  )}
                  
                  {selectedPayment.stripePaymentIntentId && !selectedPayment.id.startsWith('booking_') && (
                    <Alert status="success" size="sm">
                      <AlertIcon />
                      <AlertDescription fontSize="sm">
                        This is a legacy Payment Intent payment. 
                        Notes added here are for internal reference only.
                      </AlertDescription>
                    </Alert>
                  )}
                </>
              )}
              
              <FormControl>
                <FormLabel>Payment Notes</FormLabel>
                <Textarea
                  value={editingNotes}
                  onChange={(e) => setEditingNotes(e.target.value)}
                  placeholder="Add notes about this payment (e.g., payment method, reference number, special circumstances)..."
                  rows={4}
                />
                <Text fontSize="xs" color="gray.500" mt={1}>
                  These notes are stored with the payment record and can be used to track non-Stripe payments or add important payment details.
                </Text>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onEditClose}>
              Cancel
            </Button>
            <Button 
              colorScheme="blue" 
              onClick={handleUpdatePayment}
              isLoading={updatingPayment}
              loadingText="Updating"
            >
              Update Payment
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Process Payment Modal */}
      <Modal isOpen={isProcessOpen} onClose={() => setIsProcessOpen(false)} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Process New Payment</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <Alert status="info">
                <AlertIcon />
                <Box>
                  <AlertTitle>Create Payment Intent</AlertTitle>
                  <AlertDescription>
                    This will create a Stripe payment intent that can be used to collect payment from the customer.
                  </AlertDescription>
                </Box>
              </Alert>

              <FormControl isRequired>
                <FormLabel>Customer Name</FormLabel>
                <Input
                  value={processForm.customerName}
                  onChange={(e) => handleProcessFormChange('customerName', e.target.value)}
                  placeholder="Enter customer name"
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Customer Email</FormLabel>
                <Input
                  type="email"
                  value={processForm.customerEmail}
                  onChange={(e) => handleProcessFormChange('customerEmail', e.target.value)}
                  placeholder="Enter customer email"
                />
              </FormControl>

              <HStack spacing={4} w="full">
                <FormControl isRequired>
                  <FormLabel>Amount ($)</FormLabel>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={processForm.amount}
                    onChange={(e) => handleProcessFormChange('amount', e.target.value)}
                    placeholder="0.00"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Currency</FormLabel>
                  <Select
                    value={processForm.currency}
                    onChange={(e) => handleProcessFormChange('currency', e.target.value)}
                  >
                    <option value="usd">USD</option>
                    <option value="eur">EUR</option>
                    <option value="gbp">GBP</option>
                    <option value="cad">CAD</option>
                    <option value="aud">AUD</option>
                  </Select>
                </FormControl>
              </HStack>

              <FormControl>
                <FormLabel>Booking ID (Optional)</FormLabel>
                <Input
                  value={processForm.bookingId}
                  onChange={(e) => handleProcessFormChange('bookingId', e.target.value)}
                  placeholder="Enter booking ID if applicable"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Payment Method</FormLabel>
                <Select
                  value={processForm.paymentMethod}
                  onChange={(e) => handleProcessFormChange('paymentMethod', e.target.value)}
                >
                  <option value="card">Credit/Debit Card</option>
                  <option value="bank_transfer">Bank Transfer</option>
                  <option value="cash">Cash</option>
                  <option value="check">Check</option>
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel>Description</FormLabel>
                <Textarea
                  value={processForm.description}
                  onChange={(e) => handleProcessFormChange('description', e.target.value)}
                  placeholder="Enter payment description or notes..."
                  rows={3}
                />
              </FormControl>

              <Alert status="warning">
                <AlertIcon />
                <Box>
                  <AlertTitle>Important</AlertTitle>
                  <AlertDescription>
                    After creating the payment intent, you'll receive a client secret that can be used to complete the payment on the frontend.
                  </AlertDescription>
                </Box>
              </Alert>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={() => setIsProcessOpen(false)}>
              Cancel
            </Button>
            <Button 
              colorScheme="green" 
              onClick={handleProcessPayment}
              isLoading={processing}
              loadingText="Creating Payment Intent"
            >
              Create Payment Intent
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default PaymentManagement; 